// frontend/src/utils/download.js

/**
 * Name a downloaded export: `base_YYYY-MM-DD.ext`, dated the store's day.
 *
 * The stamp answers "which of my business days is this export from", and the
 * merchant's business day is the store's — the same day the rest of the app
 * shows them, not the UTC day.
 *
 * @param {string} base   filename stem, no extension (e.g. 'promotions').
 * @param {string} format file extension (e.g. 'xlsx').
 * @param {string} today  the store's today as 'YYYY-MM-DD' (useStoreToday).
 *   On '' the date segment is OMITTED rather than filled from a clock — an
 *   undated export is honest, a wrongly-dated one is not.
 * @returns {string} the download filename, including extension.
 */
export function exportFilename(base, format, today) {
  return today ? `${base}_${today}.${format}` : `${base}.${format}`;
}

/**
 * Name one purchase order's download, by kind: 'pdf' | 'csv' | 'xlsx'.
 *
 * The PDF IS the purchase order, so it takes the PO number bare; the CSV/Excel
 * is a file ABOUT it — its items — so it takes the `_items` tail. That tail is
 * a contract with the backend, which writes the same name into
 * Content-Disposition (`_po_lines_dataset` in purchase_orders/po_viewset.py).
 *
 * Undated, unlike `exportFilename` above: a list export answers "which of my
 * business days is this from", while a PO download is one identified document
 * — re-downloading PO-2026-0042 should give the merchant the same file name,
 * not a new one each day.
 */
export function poDownloadFilename(poNumber, kind) {
  return kind === 'pdf' ? `${poNumber}.pdf` : `${poNumber}_items.${kind}`;
}

/**
 * Reduce one filename segment to `[A-Za-z0-9._-]`, collapsing runs.
 *
 * An allow-list rather than a strip-the-bad-ones list: the set of characters
 * that survive every filesystem and browser is far smaller than the set anyone
 * would think to exclude, and supplier names are merchant-entered free text.
 * Mirrors `sanitize_filename_part` in backend/purchase_orders/utils.py.
 */
const filenamePart = (text) =>
  (String(text ?? '').trim().replace(/[^A-Za-z0-9._-]+/g, '-').replace(/^[-.]+|[-.]+$/g, '')
    || 'export');

/**
 * Name one PO recommendation's items download.
 *
 * A purchase order names its own file — it has a PO number. A recommendation
 * has none: it is engine output identified by WHO to order from and WHEN, so
 * the name is built from exactly that (`Acme-Supply_2026-09-14_items.csv`). A
 * merchant downloading three on the same morning gets three distinct files
 * rather than `export (2).csv`.
 *
 * There is no `pdf` kind, unlike `poDownloadFilename`: a recommendation is a
 * proposal, not a document anyone has committed to.
 *
 * The backend builds the same stem for Content-Disposition
 * (`recommendation_export_basename` in backend/purchase_orders/utils.py) —
 * change the shape in one and change it in the other.
 */
export function recommendationDownloadFilename(supplierName, orderDate, format) {
  return `${filenamePart(supplierName)}_${filenamePart(orderDate)}_items.${format}`;
}

/**
 * Follow a link the page never rendered: a synthetic click on a throwaway
 * anchor. The one mechanism for handing the browser a file (below) or a
 * `mailto:` (utils/mailto.js) — unlike `window.open`, it still works after an
 * await has spent the click's user gesture, so a popup blocker cannot eat it.
 *
 * @param {{ href: string, download?: string }} attributes  anchor attributes.
 */
export function clickAnchor(attributes) {
  const link = Object.assign(document.createElement('a'), attributes);
  // Firefox requires the anchor to be in the document for a synthetic click.
  document.body.appendChild(link);
  link.click();
  link.remove();
}

/**
 * Trigger a browser download of a fetched Response's body.
 *
 * The single home for the blob → object-URL → anchor-click → revoke dance.
 * The caller checks `res.ok` first and reports failure its own way; this owns
 * only the download mechanics.
 *
 * @param {Response} res      A fetch Response whose body is the file to save.
 * @param {string}   filename Download filename, including extension.
 */
export async function downloadBlobResponse(res, filename) {
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  try {
    clickAnchor({ href: url, download: filename });
  } finally {
    URL.revokeObjectURL(url);
  }
}
